import React, { useState, useEffect } from 'react';
import { BarChart3, Trash2, RefreshCw, Sparkles, Plus } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import Button from '../components/common/Button';
import ChartRenderer from '../components/charts/ChartRenderer';
import { useReportWidgets } from '../hooks/useReportWidgets';
import { useWidgetExecution } from '../hooks/useWidgetExecution';
import { useNaturalLanguageWidget } from '../hooks/useNaturalLanguageWidget';

const ReportsPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [nlQuery, setNlQuery] = useState('');

  // Custom hooks
  const { widgets, isLoading, error, createWidget, deleteWidget, isCreating } = useReportWidgets(true);
  const { executeWidgets, getWidgetData, isWidgetExecuting, clearWidgetData, executeWidget } = useWidgetExecution();
  const { createWidgetFromNaturalLanguage, isGenerating } = useNaturalLanguageWidget();

  // Auto-load data for all widgets when they're loaded
  useEffect(() => {
    if (widgets.length > 0 && !isLoading) {
      executeWidgets(widgets);
    }
  }, [widgets, isLoading, executeWidgets]);

  // Handle widget creation from natural language
  const handleCreateWidget = async () => {
    try {
      await createWidgetFromNaturalLanguage(nlQuery, createWidget);
      setNlQuery('');
      setShowCreateModal(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create widget');
    }
  };

  // Handle widget deletion
  const handleDeleteWidget = (widgetId: number) => {
    deleteWidget(widgetId);
    clearWidgetData(widgetId);
  };

  // Render widget content
  const renderWidgetContent = (widget: any) => {
    const data = getWidgetData(widget.id);
    const isExecuting = isWidgetExecuting(widget.id);

    if (isExecuting) {
      return (
        <div className="widget-content">
          <div className="widget-loading">
            <RefreshCw className="animate-spin" size={24} />
            <span>Loading data...</span>
          </div>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="widget-content">
          <div className="widget-no-data">
            <BarChart3 size={24} />
            <span>No data available</span>
          </div>
        </div>
      );
    }

    return (
      <div className="widget-content">
        <ChartRenderer widget={widget} data={data} />
      </div>
    );
  };

  return (
    <PageLayout title="AI-Powered Reports Dashboard">
      <div className="reports-container">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-content">
            <h1 className="page-title">
              <Sparkles size={24} style={{ marginRight: '12px' }} />
              AI-Powered Reports Dashboard
              <span className="page-count">{widgets?.length || 0} widgets</span>
            </h1>
            
            <div className="page-actions">
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowCreateModal(true)}
                disabled={isLoading}
              >
                <Sparkles size={16} style={{ marginRight: '8px' }} />
                Ask AI to Create Widget
              </Button>
            </div>
          </div>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0 0 0' }}>
            Create custom reports using natural language. Just describe what you want to see!
          </p>
        </div>

        {/* Widgets Grid */}
        <div className="widgets-grid">
          {isLoading ? (
            <div className="widgets-loading">
              <RefreshCw className="animate-spin" size={32} />
              <span>Loading widgets...</span>
            </div>
          ) : error ? (
            <div className="widgets-error">
              <h3>Error loading widgets</h3>
              <p>{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
            </div>
          ) : widgets.length === 0 ? (
            <div className="widgets-empty">
              <BarChart3 size={48} />
              <h3>No widgets yet</h3>
              <p>Create your first AI-powered report widget by clicking the button above.</p>
            </div>
          ) : (
            widgets.map((widget) => (
              <div key={widget.id} className="widget-card">
                <div className="widget-header">
                  <div className="widget-title">
                    <h3>{widget.name}</h3>
                    <span className="widget-type">{widget.visualization_type}</span>
                  </div>
                  <div className="widget-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => executeWidget(widget)}
                      disabled={isWidgetExecuting(widget.id)}
                    >
                      <RefreshCw size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteWidget(widget.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                
                <div className="widget-body">
                  {renderWidgetContent(widget)}
                </div>
                
                <div className="widget-footer">
                  <div className="widget-query">
                    <strong>Query:</strong> {widget.nl_query}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Add Widget Placeholder */}
          <div 
            className="widget-card add-widget-card"
            onClick={() => setShowCreateModal(true)}
          >
            <div className="add-widget-content">
              <Plus size={48} className="add-icon" />
              <h3>Create New Widget</h3>
              <p>Ask AI to generate a custom report widget</p>
            </div>
          </div>
        </div>

        {/* Create Widget Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <Sparkles size={20} style={{ marginRight: '8px' }} />
                  Create AI-Powered Widget
                </h2>
                <button 
                  className="modal-close-button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isGenerating || isCreating}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-content">
                <p className="modal-description">
                  Describe what you want to see in plain English and we'll create the perfect widget for you.
                </p>
                
                <div className="form-group">
                  <label>What would you like to analyze?</label>
                  <textarea
                    className="form-textarea"
                    rows={4}
                    placeholder="e.g., Show me assessment completion rates by supplier tier over the last 6 months"
                    value={nlQuery}
                    onChange={(e) => setNlQuery(e.target.value)}
                    disabled={isGenerating || isCreating}
                  />
                </div>
                
                <div className="example-queries">
                  <p><strong>Try these examples:</strong></p>
                  <div className="examples-list">
                    <button 
                      className="example-button"
                      onClick={() => setNlQuery('Show me total assets by account category')}
                      disabled={isGenerating || isCreating}
                    >
                      Show me total assets by account category
                    </button>
                    <button 
                      className="example-button"
                      onClick={() => setNlQuery('Percentage of Total Assets by Account Category')}
                      disabled={isGenerating || isCreating}
                    >
                      Percentage of Total Assets by Account Category
                    </button>
                    <button 
                      className="example-button"
                      onClick={() => setNlQuery('Display balance changes over time by account type')}
                      disabled={isGenerating || isCreating}
                    >
                      Display balance changes over time by account type
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowCreateModal(false)}
                  disabled={isGenerating || isCreating}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleCreateWidget}
                  disabled={!nlQuery.trim() || isGenerating || isCreating}
                >
                  {isGenerating || isCreating ? (
                    <>
                      <RefreshCw className="animate-spin" size={16} style={{ marginRight: '8px' }} />
                      {isGenerating ? 'AI is thinking...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} style={{ marginRight: '8px' }} />
                      Create Widget
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ReportsPage;
